/**
 * The wait for a session token a source is minting.
 *
 * A page opens before its first session token is minted, and every read it starts in that window
 * would go out under the publishable key alone, which the API answers with the anonymous
 * projection. A request made while the credential's {@link SessionTokenSource} holds no token and
 * reports one on its way therefore holds the source for the span of a wait: it subscribes, which
 * starts the mint of an engine nobody holds yet, and it is sent once the source holds a token or
 * reports none on its way.
 *
 * The wait reads the snapshot exactly as `credentialScope` does (a token held, or `isLoading`),
 * so a read keyed under a scope that says no session is sent under the key alone, and one keyed
 * under a scope that says a session waits for it.
 *
 * The rule lives in the transport rather than in a binding, so every caller of the client (a
 * component, a worker, a CLI) and every port of it waits the same way.
 */

import type { SessionTokenSource } from "./auth.js";
import { MountayaConnectionError } from "./errors.js";
import { isAborted } from "./responseBody.js";
import { abortReasonOf } from "./retry.js";

/** What bounds one wait, and the operation its refusal names. */
interface SessionWaitBounds {
  readonly operationId: string;
  readonly signal?: AbortSignal | undefined;
  readonly timeoutMs: number;
}

/**
 * waitForSessionToken answers `undefined` where the source holds a token or reports none on its
 * way, so neither case costs a turn of the event loop or a subscription, and otherwise a promise
 * that settles once a request made now has nothing left to wait for: the source holds a token,
 * or reports none on its way.
 *
 * The source is subscribed for the span of the wait and released as it ends, however it ends.
 *
 * @throws (as a rejection) the caller's abort reason once `signal` aborts, and a
 *   `MountayaConnectionError` naming the operation once `timeoutMs` passes with a mint still out.
 */
export const waitForSessionToken = (
  source: SessionTokenSource,
  bounds: SessionWaitBounds,
): Promise<void> | undefined => {
  const held = source.getSnapshot();
  if (held.token !== undefined || !held.isLoading) {
    return undefined;
  }

  const { operationId, signal, timeoutMs } = bounds;
  if (signal !== undefined && isAborted(signal)) {
    return Promise.reject(abortReasonOf(signal));
  }

  return new Promise<void>((resolve, reject) => {
    let ended = false;
    let release: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const end = (settle: () => void): void => {
      if (ended) {
        return;
      }

      ended = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      release?.();
      settle();
    };

    const onAbort = (): void => {
      end(() => {
        reject(signal === undefined ? undefined : abortReasonOf(signal));
      });
    };

    const check = (): void => {
      const snapshot = source.getSnapshot();
      if (snapshot.token !== undefined || !snapshot.isLoading) {
        end(resolve);
      }
    };

    // A source may tell its listener inside `subscribe` (an engine reports the mint it starts),
    // so the wait can end before the release is in hand: it is then released here.
    const unsubscribe = source.subscribe(check);
    if (ended) {
      unsubscribe();

      return;
    }

    release = unsubscribe;
    check();
    if (ended) {
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });
    timer = setTimeout(() => {
      end(() => {
        reject(
          new MountayaConnectionError(
            `${operationId}: no session token within ${timeoutMs} ms: the session token mint has not answered. Raise timeoutMs, or check the endpoint the mint calls.`,
          ),
        );
      });
    }, timeoutMs);
  });
};
